import { useMemo, useState } from 'react';
import { Button, Input } from '../../../design-system';
import { money, basisLabel } from '../../../lib/retainer';

// Picking what a retainer covers, out of the firm's own service catalog.
//
// ⚠️ THE RATE IS SHOWN BEFORE THE SERVICE IS ADDED, not after. A firm building
// a package is deciding what it can afford to include, and a picker that made
// you add a line to find out what it costs would have people adding and
// removing rows to read a price. The rate comes from utils/servicePrice's
// resolution — the firm's own override where it has one, the catalog's
// indicative figure otherwise — and is copied onto the line, never referenced,
// so editing the rate card later cannot re-price a signed agreement.
//
// An unpriced service is offered anyway, at 0, because most of the seeded
// catalog is unpriced and refusing to list those would make the picker useless
// on day one. The row then just needs a number typed into it.

function ServicePicker({ services, chosenIds, onAdd, onAddCustom, currency }) {
  const [q, setQ] = useState('');
  const [custom, setCustom] = useState('');

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = [];
    const byName = new Map();
    services.forEach((s) => {
      if (chosenIds.has(s.id)) return;
      if (needle && !`${s.name} ${s.groupName}`.toLowerCase().includes(needle)) return;
      if (!byName.has(s.groupName)) {
        const g = { name: s.groupName, items: [] };
        byName.set(s.groupName, g);
        out.push(g);
      }
      byName.get(s.groupName).items.push(s);
    });
    return out;
  }, [services, chosenIds, q]);

  const addCustom = () => {
    const name = custom.trim();
    if (!name) return;
    onAddCustom(name);
    setCustom('');
  };

  return (
    <div className="ret-picker">
      <Input
        label="Add services"
        placeholder="Search the catalog…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      <div className="ret-picker-list">
        {groups.length === 0 && (
          <p className="ret-empty">
            {q ? 'Nothing matches that.' : 'Everything in the catalog is already on this retainer.'}
          </p>
        )}
        {groups.map((g) => (
          <div key={g.name} className="ret-picker-group">
            <div className="ret-picker-group-name">{g.name}</div>
            {g.items.map((s) => (
              <button
                key={s.id}
                type="button"
                className="ret-picker-item"
                onClick={() => onAdd(s)}
              >
                <span className="ret-picker-item-name">{s.name}</span>
                <span className="ret-picker-item-price">
                  {s.price
                    ? `${money(s.price.amount, currency)} ${basisLabel(s.price.billingBasis)}`
                    : 'No rate set'}
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* The escape hatch. A practice's arrangements will never all be in a
          catalog, and a firm that hits that wall starts bending an unrelated
          service to fit — the failure Expirable's `Other` type exists to stop. */}
      <div className="ret-picker-custom">
        <Input
          label="Or something not in the catalog"
          placeholder="e.g. Monthly bookkeeping"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
        />
        <Button variant="secondary" size="sm" onClick={addCustom} disabled={!custom.trim()}>Add</Button>
      </div>
    </div>
  );
}

export default ServicePicker;
